import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import {Location} from '@angular/common';
import { TopPropertyTypeModel } from 'src/app/core/linked-tables/_models/top-property-type.model';
import { selectLastCreatedTopPropertyTypeId, selectTopPropertyTypeById, selectTopPropertyTypesActionLoading } from 'src/app/core/linked-tables/_selectors/top-property-type.selectors';
import { TopPropertyTypeOnServerCreated, TopPropertyTypeUpdated } from 'src/app/core/linked-tables/_actions/top-property-type.actions';
import {combineLatest} from 'rxjs'

@Component({
    selector: 'kt-top-property-type-edit',
    templateUrl: './top-property-type-edit.component.html',
    styleUrls: ['./top-property-type-edit.component.scss']
})
export class TopPropertyTypeEditComponent implements OnInit, OnDestroy {

    model: TopPropertyTypeModel;
    oldModel: TopPropertyTypeModel;

    topPropertyTypeForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = false;
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectTopPropertyTypesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectTopPropertyTypeById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initTopPropertyType();
                    }
                });
            } else {
                this.model = new TopPropertyTypeModel();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initTopPropertyType();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Bases of Values', page: `../default/admin-management/linked-tables/base-of-values`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/base-of-values/edit/${id}`
                    : `../default/admin-management/linked-tables/base-of-values/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initTopPropertyType() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.topPropertyTypeForm = this.fb.group({
            name: [this.model.title, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('TOP_PROPERTY_TYPE.FORM.TITLE.EDIT', {name: this.model.title});
        }

        return this.translate.instant('TOP_PROPERTY_TYPE.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.topPropertyTypeForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.topPropertyTypeForm.markAsPristine();
        this.topPropertyTypeForm.markAsUntouched();
        this.topPropertyTypeForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.topPropertyTypeForm.controls;
        /** check form */
        if (this.topPropertyTypeForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateTopPropertyType(this.model);
        } else {
            this.createTopPropertyType(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: TopPropertyTypeModel
     */
    updateTopPropertyType(_model: TopPropertyTypeModel) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateTopPropertyType: Update<TopPropertyTypeModel> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new TopPropertyTypeUpdated({
            partialItem: updateTopPropertyType,
            item: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: TopPropertyTypeModel
     */
    createTopPropertyType(_model: TopPropertyTypeModel) {
        this.store.dispatch(new TopPropertyTypeOnServerCreated({item: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedTopPropertyTypeId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.title && this.model.title.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.title != this.oldModel.title;
        }
        return false;
    }
}
